/* ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THIS FILE IS A COPY. THE ORIGINAL LIVES IN THE WEB REPO:
 *     HypeTunes_Web_2026/src/components/three/clipRetarget.js
 *
 * Plays a clip authored on one rig on any body (the shared clip library,
 * services/clipLibrary.js). One implementation, shared like faceRig/faceDecal.
 *
 * ⚠ DO NOT EDIT BELOW THE SENTINEL. Fix the Web original, then:
 *     node scripts/check_face_decal_sync.mjs --sync
 * ───────────────────────────────────────────────────────────────────────────── */

/* ── VERBATIM FROM HypeTunes_Web_2026 BELOW THIS LINE ── */
import * as THREE from 'three'

/**
 * ONE CLIP, EVERY BODY — play an animation authored on one rig on any other.
 *
 * Every body comes out of its own rig run, and the rigger re-rolls the rest pose
 * each time: the same bone can rest 115–173° apart between two bodies (measured
 * 2026-10-28, RightUpLeg). Played as-is, a borrowed clip fuses the legs and
 * throws the arms; that is why clips used to be "only valid on the rig run they
 * came from", and why adding one meant re-rigging the body.
 *
 * The fix is to play the clip's CHANGE from its own rest on top of the target's
 * rest, per bone — the change each bone makes in the SKELETON's own space, so
 * every part of the body turns exactly as it did on the rig the clip came from:
 *
 *     target(t) = (Pt⁻¹ · Ps) · source(t) · (Ws⁻¹ · Wt)
 *
 * W = the bone's rest orientation from the skeleton root, P = its parent's,
 * s = the clip's rig, t = the body. Both brackets are constant per bone.
 *
 * ⚠ IT WAS `targetRest · sourceRest⁻¹ · source(t)` UNTIL 2026-11-04, which is
 * the formula above only when a bone's own axes point the same way on both rigs.
 * They mostly do, so it passed by eye on four bodies (FINDINGS §10) — but the
 * rigger sets the HEAD's axes per run: 128° apart between the library mannequin
 * and Lyra (99), and her Formal Bow threw her head BACK. Measured per bone
 * against the mannequin (world change from rest, four moves × four moments):
 * the old form was off by up to 69° at the head and 21° at an arm; this one is
 * off by 0°.
 *
 * The source rest travels inside every clip GLB (its node table), so a clip
 * needs nothing but its own file.
 *
 * ⚠ CAPTURE THE TARGET REST BEFORE ANY CLIP TOUCHES A BONE. The mixer rewrites
 * bones every frame, so a rest read later is whatever was playing.
 * ⚠ HIPS PLAY IN PLACE: x/z are pinned to the target's rest (rigger clips carry
 * root motion in centimetres from wherever the animator stood) and only the
 * vertical moves, shifted by the difference in hip height between the rigs.
 * ⚠ What it cannot fix is a body's own skinning: a mesh built with its arms
 * welded to its sides tears on an arm raise however the clip arrives.
 */

/* A node's orientation from `root` down, leaving out `root`'s own transform (the
   scene places the body; the skeleton's space starts below it). */
function fromRoot(node, root) {
  const q = new THREE.Quaternion()
  for (let o = node; o && o !== root; o = o.parent) q.premultiply(o.quaternion)
  return q
}

/* `q`/`p` = local rest, `w` = the node's rest orientation in skeleton space, `pw` = its parent's. */
function restOf(o, root) {
  return { q: o.quaternion.clone(), p: o.position.clone(), w: fromRoot(o, root), pw: fromRoot(o.parent, root) }
}

/** Rest transform of every bone under `root`. Call BEFORE the mixer runs. */
export function captureRest(root) {
  const rest = new Map()
  root?.traverse((o) => { if (o.isBone) rest.set(o.name, restOf(o, root)) })
  return rest
}

/**
 * @param {THREE.AnimationClip} clip  the clip as loaded (left untouched; a clone is returned)
 * @param {THREE.Object3D} sourceRoot the clip GLB's scene — its nodes ARE the source rig's rest
 * @param {Map} targetRest            captureRest(body), taken before any clip played
 * @param {{ pinXZ?: boolean }} opts
 * @returns {{ clip: THREE.AnimationClip, maxRestGapDeg: number, worstBone: string }}
 */
export function retargetClip(clip, sourceRoot, targetRest, { pinXZ = true } = {}) {
  const out = clip.clone()
  /* ⚠ READ THE SOURCE BEFORE ANYTHING PLAYS IT. Its nodes ARE its rest only
     while no mixer has moved them — this is the clip file's own scene, never a
     body on screen. */
  const srcRest = new Map()
  sourceRoot?.traverse((o) => { if (o !== sourceRoot) srcRest.set(o.name, restOf(o, sourceRoot)) })
  out.tracks = out.tracks.filter((t) => !t.name.endsWith('.scale'))
  const pre = new THREE.Quaternion(); const post = new THREE.Quaternion(); const q = new THREE.Quaternion()
  let maxRestGapDeg = 0; let worstBone = ''
  for (const tr of out.tracks) {
    const { nodeName, propertyName } = THREE.PropertyBinding.parseTrackName(tr.name)
    const t = targetRest.get(nodeName); const s = srcRest.get(nodeName)
    if (!t || !s) continue
    if (propertyName === 'quaternion') {
      const gap = 2 * Math.acos(Math.min(1, Math.abs(t.q.dot(s.q)))) * 180 / Math.PI
      if (gap > maxRestGapDeg) { maxRestGapDeg = gap; worstBone = nodeName }
      if (t.w) {
        pre.copy(t.pw).invert().multiply(s.pw)
        post.copy(s.w).invert().multiply(t.w)
      } else {
        // A rest map from before 2026-11-04 (local only): the old form.
        pre.copy(t.q).multiply(s.q.clone().invert())
        post.identity()
      }
      const v = tr.values
      for (let i = 0; i < v.length; i += 4) q.fromArray(v, i).premultiply(pre).multiply(post).toArray(v, i)
    } else if (propertyName === 'position') {
      const v = tr.values
      for (let i = 0; i < v.length; i += 3) {
        if (pinXZ) { v[i] = t.p.x; v[i + 2] = t.p.z }
        v[i + 1] += t.p.y - s.p.y
      }
    }
  }
  return { clip: out, maxRestGapDeg, worstBone }
}

export default retargetClip
